import { Button, useTranslation } from '@hbs-us/ui';
import { useState } from 'react';

import { ApiError } from '../../lib/apiFetch.js';
import { ConfirmDialog } from '../_money/ConfirmDialog.js';
import type { ShiftConflict, TimesheetGridRow } from './contractsMirror.js';
import { type ClockTarget, labelForTarget, SiteCombobox } from './SiteCombobox.js';
import { useClockInForMutation } from './useApprovalMutation.js';
import { useClockStateQuery } from './useClockMutation.js';

/**
 * Start somebody else's shift — "clock Nora in at 7:30, it is already 8:30".
 *
 * What this creates is a REAL open shift, not a record of one, so the crew member clocks themselves
 * out of it at the end of the day and the hours are priced by the ordinary punch path. That is why
 * there is no clock-out field here: this form starts a shift, it does not write one.
 *
 * It reuses `SiteCombobox`, so a supervisor can punch someone against an assigned job OR a typed
 * address exactly as the crew can, with the same never-auto-select behaviour. Typed text still goes
 * behind a confirm, because that is the path that turns a typo into a permanent site in the office's
 * job list.
 *
 * Gated on `time.approve` by the caller, and re-checked by the server. Hiding it is UX; the endpoint
 * is the authorization.
 *
 * The distinct server errors are surfaced by name rather than collapsed into "something went wrong".
 * `overlaps_existing` in particular carries the shift it collided with, and "that overlaps something"
 * is unactionable — the supervisor needs to know WHICH shift is in the way.
 */
export interface StartShiftForProps {
  /** Active employees for the week; the grid already carries every one of them. */
  rows: TimesheetGridRow[];
}

const CONTROL =
  'mt-1 h-10 w-full min-w-0 rounded-md border border-border bg-background px-3 text-foreground text-sm ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ' +
  'focus-visible:ring-offset-background';

export function StartShiftFor({ rows }: StartShiftForProps) {
  const { t } = useTranslation();
  const mutation = useClockInForMutation();
  // The job list the crew clock uses — a hardcoded whitelist with no money on it. Never point a
  // crew-facing picker at GET /jobs, which decides what to return from a financials.view boolean.
  const clockState = useClockStateQuery(true);

  const [open, setOpen] = useState(false);
  const [employeeId, setEmployeeId] = useState('');
  const [startLocal, setStartLocal] = useState('');
  const [reason, setReason] = useState('');
  const [target, setTarget] = useState<ClockTarget | null>(null);
  const [confirmingTyped, setConfirmingTyped] = useState(false);

  const conflict =
    mutation.error instanceof ApiError
      ? ((mutation.error.details?.conflict ?? null) as ShiftConflict | null)
      : null;
  const errorMessage =
    mutation.error instanceof ApiError
      ? t(`timesheets:startShift.errors.${mutation.error.message}`, {
          defaultValue: t('timesheets:startShift.errors.generic'),
        })
      : null;

  const ready = Boolean(employeeId) && Boolean(target) && reason.trim().length > 0;

  function submitPunch(chosen: ClockTarget) {
    mutation.mutate(
      {
        employeeId: Number(employeeId),
        editReason: reason.trim(),
        // An empty time means "start them now", which is the ordinary case. datetime-local is
        // wall-clock, so it becomes an instant before it leaves the browser.
        ...(startLocal ? { punchedAt: new Date(startLocal).toISOString() } : {}),
        ...(chosen.kind === 'job'
          ? { jobId: chosen.job.id }
          : chosen.kind === 'site'
            ? {
                siteAddress: chosen.suggestion.address,
                siteLat: chosen.suggestion.lat,
                siteLng: chosen.suggestion.lng,
              }
            : { siteAddress: chosen.address }),
      },
      {
        onSuccess: () => {
          setConfirmingTyped(false);
          setOpen(false);
          setEmployeeId('');
          setStartLocal('');
          setReason('');
          setTarget(null);
        },
      },
    );
  }

  if (!open) {
    return (
      <div className="mb-4">
        <Button type="button" variant="secondary" onClick={() => setOpen(true)}>
          {t('timesheets:startShift.open')}
        </Button>
      </div>
    );
  }

  return (
    <section
      aria-label={t('timesheets:startShift.regionLabel')}
      className="mb-4 rounded-md border border-border bg-card p-4"
    >
      <h2 className="mb-1 font-semibold text-foreground text-sm">
        {t('timesheets:startShift.heading')}
      </h2>
      <p className="mb-3 text-muted-foreground text-xs">{t('timesheets:startShift.hint')}</p>

      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (!target || !ready) return;
          // Only text nobody geocoded gets a dialog: that is the one that turns a typo into a
          // permanent site. A picked suggestion already said so inline.
          if (target.kind === 'typed') {
            setConfirmingTyped(true);
            return;
          }
          submitPunch(target);
        }}
      >
        <div className="w-full sm:w-56">
          <label htmlFor="start-for-employee" className="font-medium text-foreground text-xs">
            {t('timesheets:startShift.employeeLabel')}
          </label>
          <select
            id="start-for-employee"
            className={CONTROL}
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            required
          >
            <option value="">{t('timesheets:startShift.employeePlaceholder')}</option>
            {rows.map((row) => (
              <option key={row.employeeId} value={row.employeeId}>
                {row.employeeName}
              </option>
            ))}
          </select>
        </div>

        <div className="w-full sm:w-56">
          <label htmlFor="start-for-time" className="font-medium text-foreground text-xs">
            {t('timesheets:startShift.startedAtLabel')}
          </label>
          <input
            id="start-for-time"
            type="datetime-local"
            className={CONTROL}
            value={startLocal}
            onChange={(e) => setStartLocal(e.target.value)}
          />
        </div>

        <div className="w-full min-w-0 sm:flex-1">
          <SiteCombobox jobs={clockState.data?.jobs ?? []} onChange={setTarget} />
        </div>

        <div className="w-full min-w-0">
          <label htmlFor="start-for-reason" className="font-medium text-foreground text-xs">
            {t('timesheets:startShift.reasonLabel')}
          </label>
          <input
            id="start-for-reason"
            type="text"
            className={CONTROL}
            maxLength={500}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
          />
        </div>

        <Button type="submit" disabled={mutation.isPending || !ready}>
          {t('timesheets:startShift.submit')}
        </Button>
        <Button type="button" variant="secondary" onClick={() => setOpen(false)}>
          {t('timesheets:startShift.cancel')}
        </Button>
      </form>

      {errorMessage ? (
        <p role="alert" aria-live="assertive" className="mt-3 text-destructive text-sm">
          {errorMessage}
          {conflict ? (
            <span className="mt-1 block font-mono text-xs">
              {t('timesheets:startShift.conflictDetail', {
                date: conflict.date,
                from: conflict.clockInAt,
                to: conflict.clockOutAt ?? t('timesheets:startShift.stillRunning'),
              })}
            </span>
          ) : null}
        </p>
      ) : null}

      <ConfirmDialog
        open={confirmingTyped && target?.kind === 'typed'}
        heading={t('timesheets:startShift.confirmHeading')}
        body={t('timesheets:startShift.confirmBody', {
          address: target ? labelForTarget(target) : '',
        })}
        confirmLabel={t('timesheets:startShift.confirmConfirm')}
        cancelLabel={t('timesheets:startShift.confirmCancel')}
        confirmPending={mutation.isPending}
        onConfirm={() => {
          if (target) submitPunch(target);
        }}
        onCancel={() => setConfirmingTyped(false)}
      />
    </section>
  );
}
